import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Controller, Path } from 'react-hook-form';
import { BaseControlledInputProps } from '../basicControlledInputProps.types';
import FormFieldWrappers from '../FormFieldWrappers';
import FormErrorHelper from '../SharedComponents/FormErrorHelper';
import { parseError } from '../form-utils/errorParser.util';
import { useFormTracking } from '../hooks/useFormTracking.hook';
import { OptionsContainer } from '@act/utilities/filters/filterStyledComponents';

/**
 * Props for the CheckboxField component.
 */
export interface CheckboxFieldProps {
  /**
   * The label text for the checkbox.
   */
  label: string;

  /**
   * The default checked state of the checkbox.
   */
  defaultChecked?: boolean;

  /**
   * Callback function that is called when the checkbox is checked or unchecked.
   * @param checked - The checked state of the checkbox.
   */
  onCheck?: (checked: boolean) => void;

  /**
   * Specifies whether the checkbox is disabled.
   */
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export type ControlledCheckboxProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & CheckboxFieldProps;

export const ControlledCheckbox = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  control,
  ariaLabel,
  disabled = false,
  errors = undefined,
  onCheck,
  onChange,
  hidden = false,
  trackingName,
  sensitive = true,
}: ControlledCheckboxProps<FORM_TYPE, FIELD_NAME>) => {
  const trackingProps = useFormTracking(trackingName, sensitive);

  return (
    !hidden && (
      <FormFieldWrappers.FieldWrapper>
        <FormFieldWrappers.InputWrapper>
          <OptionsContainer>
            <FormControlLabel
              label={<Typography variant="bodyLarge">{label}</Typography>}
              control={
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...trackingProps}
                      disabled={disabled}
                      checked={!!field.value}
                      onChange={e => {
                        field.onChange(e.target.checked);
                        if (onChange) {
                          onChange(e);
                        }
                        if (onCheck) {
                          onCheck(e.target.checked);
                        }
                      }}
                      inputProps={{
                        'aria-label': ariaLabel || `${name} checkbox`,
                      }}
                    />
                  )}
                />
              }
            />
          </OptionsContainer>
        </FormFieldWrappers.InputWrapper>
        {errors && parseError(name, errors) && (
          <FormErrorHelper name={name} errors={errors} />
        )}
      </FormFieldWrappers.FieldWrapper>
    )
  );
};
